import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArcElement, CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Tooltip } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import { useWidgetVisibility } from '../../context/WidgetVisibilityContext';
import {
  bedService,
  dueService,
  expenseService,
  floorService,
  hostelService,
  invoiceService,
  roomService,
  tenantService,
} from '../../services/api';
import { Bed, Due, Expense, Floor, Invoice, Room, User } from '../../types';
import { monthBounds, sumCollectionForPeriod, sumExpensesForPeriod } from '../../utils/dashboardFinance';
import { PAYMENT_MONTH_NAMES } from '../../utils/tenantPaymentStatus';
import { computeBedOccupancyFromList } from '../../utils/bedOccupancy';
import { filterResidentTenants } from '../../utils/residentTenants';
import { isActiveTenant } from '../../utils/tenantStatus';
import {
  buildFloorRoomLookup,
  computeMonthlyExpectedCollection,
  computePendingPaymentsSummary,
  EXPENSE_CATEGORY_LABELS,
  formatINR,
  formatINRCompact,
  groupInvoicesByUserId,
  num,
  sumExpensesByCategory,
  tenantDisplayName,
  tenantLocation,
} from './reportsUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Legend);

const ReportsOverview: React.FC = () => {
  const { hostel } = useAuth();
  const { isVisible } = useWidgetVisibility();
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth());
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);

  const [analytics, setAnalytics] = useState<{
    occupancy: { totalBeds: number; occupiedBeds: number; availableBeds: number };
    revenue: { totalRevenue: number; totalDues: number };
    tenants: { activeTenants: number };
  } | null>(null);
  const [tenants, setTenants] = useState<User[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [dues, setDues] = useState<Due[]>([]);

  const period = useMemo(() => monthBounds(filterYear, filterMonth + 1), [filterYear, filterMonth]);
  const locationLookup = useMemo(() => buildFloorRoomLookup(floors, rooms), [floors, rooms]);

  useEffect(() => {
    if (!hostel?.id) {
      setLoading(false);
      return;
    }
    const hid = hostel.id;
    void (async () => {
      try {
        setLoading(true);
        const [a, t, fl, rm, b, inv, exp, d] = await Promise.all([
          hostelService.getHostelAnalytics(hid),
          tenantService.getTenantsByHostel(hid, 'ALL_USERS'),
          floorService.getFloorsByHostel(hid),
          roomService.getRoomsByHostel(hid),
          bedService.getBedsByHostel(hid),
          invoiceService.getAllInvoices(),
          expenseService.getExpensesByHostel(hid, { startDate: period.start, endDate: period.end }),
          dueService.getDuesByHostel(hid, { status: 'ALL' }),
        ]);
        setAnalytics(a);
        setTenants(filterResidentTenants(t).filter((x) => !x.isDeleted));
        setFloors(fl.filter((x) => x.isActive !== false));
        setRooms(rm.filter((x) => x.isActive !== false));
        setBeds(b.filter((x) => x.isActive !== false));
        setInvoices(inv);
        setExpenses(exp);
        setDues(d);
      } catch {
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [hostel?.id, period.start, period.end]);

  const hostelInvoices = useMemo(
    () => invoices.filter((i) => !i.isDeleted && Number(i.hostelId) === hostel?.id),
    [invoices, hostel?.id],
  );

  const invoicesByUser = useMemo(() => groupInvoicesByUserId(hostelInvoices), [hostelInvoices]);

  const monthRevenue = useMemo(
    () => sumCollectionForPeriod(hostelInvoices, hostel?.id || 0, period.start, period.end),
    [hostelInvoices, hostel?.id, period],
  );
  const monthExpenses = useMemo(
    () => sumExpensesForPeriod(expenses, period.start, period.end),
    [expenses, period],
  );
  const profit = monthRevenue - monthExpenses;

  const bedOcc = useMemo(() => computeBedOccupancyFromList(beds), [beds]);
  const totalBeds = bedOcc.totalBeds;
  const occupiedBeds = bedOcc.occupiedBeds;
  const vacantBeds = bedOcc.availableBeds;
  const occPct = totalBeds > 0 ? ((occupiedBeds / totalBeds) * 100).toFixed(1) : '0';

  const activeTenants = tenants.filter((t) => t.isActive).length;
  const vacating = tenants.filter((t) => t.isNotice).length;
  const maintenanceBeds = beds.filter((b) => String(b.bedStatus).toUpperCase() === 'MAINTENANCE').length;
  const pendingDues = dues.reduce((s, d) => s + num(d.balanceAmount), 0) || num(analytics?.revenue.totalDues);

  const pendingPayments = useMemo(
    () => computePendingPaymentsSummary(tenants.filter(isActiveTenant), invoicesByUser, 6),
    [tenants, invoicesByUser],
  );

  const monthlyExpected = useMemo(() => computeMonthlyExpectedCollection(tenants), [tenants]);

  const topDues = pendingPayments.top.map((row) => ({ tenant: row.tenant, amount: row.amount }));

  const expenseByCat = useMemo(() => sumExpensesByCategory(expenses), [expenses]);
  const expenseTotal = expenseByCat.reduce((s, c) => s + c.total, 0);

  const dailyChart = useMemo(() => {
    const daysInMonth = new Date(filterYear, filterMonth + 1, 0).getDate();
    const labels: string[] = [];
    const revenue: number[] = [];
    const expense: number[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      labels.push(String(d).padStart(2, '0'));
      const dayStart = `${filterYear}-${String(filterMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      revenue.push(sumCollectionForPeriod(hostelInvoices, hostel?.id || 0, dayStart, dayStart));
      const dayExp = expenses.filter((e) => {
        const raw = e.expenseDate || e.createdAt;
        if (!raw) return false;
        return raw.slice(0, 10) === dayStart;
      });
      expense.push(dayExp.reduce((s, e) => s + num(e.amount), 0));
    }
    return { labels, revenue, expense };
  }, [filterYear, filterMonth, hostelInvoices, hostel?.id, expenses]);

  const recentActivity = useMemo(() => {
    const items: {
      id: string;
      label: string;
      sub: string;
      amount?: number;
      ts: number;
      pending?: boolean;
    }[] = [];
    for (const inv of hostelInvoices) {
      const balance = num(inv.balanceAmount);
      if (balance > 0.01 && inv.paymentStatus !== 'PAID') {
        const dueTs = inv.dueDate ? new Date(inv.dueDate).getTime() : Date.now();
        items.push({
          id: `pending-${inv.id}`,
          label: `Pending — ${inv.name || 'Tenant'}`,
          sub: `${inv.paymentMonth || ''} ${inv.paymentYear || ''} · ${inv.paymentType || 'RENT'}`.trim(),
          amount: balance,
          ts: Number.isFinite(dueTs) ? dueTs : Date.now(),
          pending: true,
        });
      }
    }
    for (const inv of hostelInvoices) {
      if (!inv.paymentTimestamp) continue;
      const paid = num(inv.paidAmount);
      if (paid <= 0) continue;
      items.push({
        id: `inv-${inv.id}`,
        label: `Payment from ${inv.name || 'Tenant'}`,
        sub: `${inv.paymentMonth || ''} ${inv.paymentYear || ''} · ${inv.paymentType || ''}`.trim(),
        amount: paid,
        ts: new Date(inv.paymentTimestamp).getTime(),
      });
    }
    return items.sort((a, b) => b.ts - a.ts).slice(0, 8);
  }, [hostelInvoices]);

  const occupancyChart = {
    labels: ['Occupied', 'Vacant'],
    datasets: [
      {
        data: [occupiedBeds, vacantBeds],
        backgroundColor: ['#6366f1', '#e0e7ff'],
        borderWidth: 0,
      },
    ],
  };

  const revenueLineChart = {
    labels: dailyChart.labels,
    datasets: [
      {
        label: 'Revenue',
        data: dailyChart.revenue,
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.12)',
        fill: true,
        tension: 0.35,
      },
      {
        label: 'Expenses',
        data: dailyChart.expense,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.08)',
        fill: true,
        tension: 0.35,
      },
    ],
  };

  const expenseDonut = {
    labels: expenseByCat.map((c) => EXPENSE_CATEGORY_LABELS[c.category] || c.category),
    datasets: [
      {
        data: expenseByCat.map((c) => c.total),
        backgroundColor: ['#6366f1', '#8b5cf6', '#a78bfa', '#f59e0b', '#10b981', '#64748b'],
        borderWidth: 0,
      },
    ],
  };

  if (loading) {
    return <div className="reports-loading">Loading overview…</div>;
  }

  if (!hostel?.id) {
    return <div className="reports-empty">Select a hostel to view reports.</div>;
  }

  const kpis: {
    id: string;
    label: string;
    value: string;
    hint: string;
    positive?: boolean;
  }[] = [
    { id: 'reports.kpi_active_tenants', label: 'Active tenants', value: String(activeTenants), hint: `${tenants.length} total` },
    { id: 'reports.kpi_occupied_beds', label: 'Occupied beds', value: String(occupiedBeds), hint: `${occPct}% occupancy` },
    { id: 'reports.kpi_vacant_beds', label: 'Vacant beds', value: String(vacantBeds), hint: `of ${totalBeds}` },
    { id: 'reports.kpi_month_revenue', label: 'Month revenue', value: formatINRCompact(monthRevenue), hint: PAYMENT_MONTH_NAMES[filterMonth] },
    {
      id: 'reports.kpi_expected_collection',
      label: 'Expected collection',
      value: formatINRCompact(monthlyExpected.amount),
      hint: `${monthlyExpected.tenantsWithRent} tenants with rent`,
    },
    {
      id: 'reports.kpi_pending_payments',
      label: 'Pending payments',
      value: formatINRCompact(pendingPayments.totalAmount),
      hint: `${pendingPayments.tenantCount} tenants`,
    },
    { id: 'reports.kpi_dues_ledger', label: 'Dues ledger', value: formatINRCompact(pendingDues), hint: 'From dues table' },
    { id: 'reports.kpi_month_expenses', label: 'Month expenses', value: formatINRCompact(monthExpenses), hint: 'This period' },
    {
      id: 'reports.kpi_profit_loss',
      label: 'Profit / loss',
      value: formatINRCompact(profit),
      hint: profit >= 0 ? 'Surplus' : 'Deficit',
      positive: profit >= 0,
    },
    { id: 'reports.kpi_vacating', label: 'Vacating', value: String(vacating), hint: 'Notice period' },
    { id: 'reports.kpi_maintenance_beds', label: 'Maintenance beds', value: String(maintenanceBeds), hint: 'Unavailable' },
  ];

  return (
    <div className="reports-section">
      <div className="reports-section__toolbar">
        <h2 className="reports-section__heading">Overview</h2>
        <div className="reports-filters">
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(Number(e.target.value))}
            className="reports-select"
            aria-label="Month"
          >
            {PAYMENT_MONTH_NAMES.map((m, i) => (
              <option key={m} value={i}>
                {m}
              </option>
            ))}
          </select>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(Number(e.target.value))}
            className="reports-select"
            aria-label="Year"
          >
            {[filterYear - 1, filterYear, filterYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="reports-kpi-grid">
        {kpis.filter((k) => isVisible(k.id)).map((k) => (
          <div key={k.id} className="reports-kpi">
            <span className="reports-kpi__label">{k.label}</span>
            <span className={`reports-kpi__value${k.positive === false ? ' reports-kpi__value--neg' : ''}`}>
              {k.value}
            </span>
            <span className="reports-kpi__hint">{k.hint}</span>
          </div>
        ))}
      </div>

      <div className="reports-widgets-row">
        {isVisible('reports.chart_occupancy') && (
        <div className="reports-widget">
          <div className="reports-widget__head">
            <h3>Occupancy</h3>
            <span className="reports-widget__meta">{occPct}%</span>
          </div>
          <div className="reports-donut-wrap">
            <Doughnut
              data={occupancyChart}
              options={{
                cutout: '72%',
                plugins: { legend: { position: 'bottom' } },
              }}
            />
            <div className="reports-donut-center">
              <strong>{occPct}%</strong>
              <span>Occupied</span>
            </div>
          </div>
          <p className="reports-widget__foot">Total capacity: {totalBeds} beds</p>
        </div>
        )}

        {isVisible('reports.chart_revenue_expenses') && (
        <div className="reports-widget reports-widget--wide">
          <div className="reports-widget__head">
            <h3>Revenue vs expenses</h3>
            <span className="reports-widget__meta">
              {PAYMENT_MONTH_NAMES[filterMonth]} {filterYear}
            </span>
          </div>
          <Line
            data={revenueLineChart}
            options={{
              responsive: true,
              plugins: { legend: { position: 'top' } },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (v) => formatINRCompact(Number(v)),
                  },
                },
              },
            }}
          />
        </div>
        )}
      </div>

      <div className="reports-widgets-row reports-widgets-row--3">
        {isVisible('reports.list_pending_payments') && (
        <div className="reports-widget">
          <div className="reports-widget__head">
            <h3>Pending payments</h3>
            <Link to="/reports/financials" className="reports-link">
              View all
            </Link>
          </div>
          <ul className="reports-dues-list">
            {topDues.length === 0 ? (
              <li className="reports-dues-list__empty">No pending payments</li>
            ) : (
              topDues.map(({ tenant, amount }) => (
                <li key={tenant.id} className="reports-dues-list__item">
                  <div>
                    <strong>{tenantDisplayName(tenant)}</strong>
                    <span>{tenantLocation(tenant, locationLookup)}</span>
                  </div>
                  <span className="reports-dues-list__amt">{formatINR(amount)}</span>
                </li>
              ))
            )}
          </ul>
        </div>
        )}

        {isVisible('reports.chart_expense_category') && (
        <div className="reports-widget">
          <div className="reports-widget__head">
            <h3>Expense by category</h3>
          </div>
          {expenseTotal > 0 ? (
            <>
              <div className="reports-donut-wrap reports-donut-wrap--sm">
                <Doughnut
                  data={expenseDonut}
                  options={{ cutout: '65%', plugins: { legend: { display: false } } }}
                />
                <div className="reports-donut-center">
                  <strong>{formatINRCompact(expenseTotal)}</strong>
                </div>
              </div>
              <ul className="reports-legend">
                {expenseByCat.map((c) => (
                  <li key={c.category}>
                    <span>{EXPENSE_CATEGORY_LABELS[c.category] || c.category}</span>
                    <span>{formatINR(c.total)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="reports-empty-inline">No expenses this period</p>
          )}
        </div>
        )}

        {isVisible('reports.list_recent_activity') && (
        <div className="reports-widget">
          <div className="reports-widget__head">
            <h3>Recent activity</h3>
            <Link to="/invoices" className="reports-link">
              View all
            </Link>
          </div>
          <ul className="reports-activity">
            {recentActivity.length === 0 ? (
              <li className="reports-activity__empty">No recent payments</li>
            ) : (
              recentActivity.map((a) => (
                <li key={a.id} className="reports-activity__item">
                  <div>
                    <strong>{a.label}</strong>
                    <span>{a.sub}</span>
                  </div>
                  {a.amount != null && (
                    <span
                      className={
                        a.pending ? 'reports-activity__amt reports-activity__amt--pending' : 'reports-activity__amt'
                      }
                    >
                      {a.pending ? formatINR(a.amount) : `+${formatINR(a.amount)}`}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
        )}
      </div>
    </div>
  );
};

export default ReportsOverview;
